import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, CheckCircle2, ChevronRight, Clock3, MapPin, ArrowRight, BadgeCheck,
} from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import Faq from "../components/Faq";
import ContactForm from "../components/ContactForm";
import { CtaBanner } from "../components/CtaBanner";
import { SERVICES } from "../data/services";
import {
  getSubArea, nearbySubAreas, districtTitle, subAreaServiceContent,
} from "../data/subAreas";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../data/site";

export default function ServiceSubArea() {
  const { slug: serviceSlug, areaSlug } = useParams();
  const area = getSubArea(areaSlug);
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  const valid = area && service;

  const d = valid ? districtTitle(area.district) : "";
  const content = valid ? subAreaServiceContent(service, area) : null;

  useEffect(() => {
    if (content) document.title = `${content.title} | Motion Tyres 24/7`;
    return () => { document.title = "Motion Tyres | 24/7 Mobile Tyre Fitting — We Come To You"; };
  }, [content]);

  if (!valid) {
    return (
      <div className="pt-40 pb-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="subarea-service-not-found">
        <h1 className="font-display font-extrabold uppercase text-5xl">Page not found</h1>
        <Link to="/" className="text-[#FF5500] underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  const town = area.town;
  const others = SERVICES.filter((s) => s.slug !== service.slug);
  const nearby = nearbySubAreas(area);
  const waMsg = `Hi Motion Tyres — I'm interested in ${service.shortName} in ${d}. Here's my location:`;

  return (
    <div data-testid={`subarea-service-page-${service.slug}-${area.slug}`}>
      {/* HERO */}
      <section className="relative pt-32 sm:pt-40 pb-14 overflow-hidden" data-testid="subarea-service-hero">
        <div
          className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,85,0,0.16) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.nav
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
              className="flex flex-wrap items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-6"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/services/${service.slug}`} className="hover:text-white transition-colors">{service.shortName}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#FF5500]">{d}</span>
            </motion.nav>

            <Eyebrow>24/7 mobile service</Eyebrow>
            <h1 className="font-display font-extrabold uppercase leading-[0.92] text-4xl sm:text-6xl mt-4">
              <SplitLines delay={0.25} accentLast lines={[service.shortName.toUpperCase(), `IN ${d.toUpperCase()}.`]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-5 text-zinc-400 text-lg max-w-lg"
            >
              {service.tagline} Covering {area.district} and all of {town.name}.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={PHONE_TEL}
                data-testid="subarea-service-call-button"
                className="btn-glow flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em]"
              >
                <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
              </a>
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="subarea-service-whatsapp-button"
                className="flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-emerald-500/70 text-emerald-400 hover:bg-emerald-500 hover:text-black transition-colors font-telemetry text-sm uppercase tracking-[0.12em]"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="frame-cut overflow-hidden border border-[#27272A]"
          >
            <img src={service.image} alt={`${content.title}`} className="w-full h-[320px] sm:h-[440px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* FACT BAR */}
      <section className="border-y border-[#27272A] bg-[#131316]" data-testid="subarea-fact-bar">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
          {[
            { i: Clock3, t: "Response in " + d, v: town.eta },
            { i: MapPin, t: "Coverage", v: `${town.name} · ${town.postcodes}` },
            { i: BadgeCheck, t: "Brands", v: "All major tyre brands" },
            { i: Phone, t: "Dispatch line", v: PHONE_DISPLAY },
          ].map(({ i: Icon, t, v }) => (
            <div key={t} className="bg-[#131316] px-6 py-6">
              <Icon className="w-4 h-4 text-[#FF5500] mb-2" />
              <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t}</p>
              <p className="font-display font-bold uppercase text-lg text-white mt-1">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="subarea-intro-section">
        <div className="max-w-3xl">
          <FadeUp>
            <Eyebrow>In detail — {d}</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-8 leading-[0.95]">
              {content.title}.
            </h2>
          </FadeUp>
          <FadeUp delay={0.05}>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-6">{content.p1}</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">{content.p2}</p>
          </FadeUp>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="subarea-included-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <FadeUp>
            <Eyebrow>Every visit includes</Eyebrow>
            <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl mt-3 mb-10">
              Nothing left to chance.
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.included.map((item, i) => (
              <FadeUp key={item} delay={i * 0.04} className="flex items-start gap-3 bg-[#131316] border border-[#27272A] px-5 py-4">
                <CheckCircle2 className="w-5 h-5 text-[#FF5500] shrink-0 mt-0.5" />
                <span className="text-zinc-300">{item}</span>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="subarea-faq-section">
        <FadeUp>
          <Eyebrow>{d} questions</Eyebrow>
          <h3 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-10">
            Local answers.
          </h3>
        </FadeUp>
        <Faq items={content.faqs} prefix={`sub-${service.slug}-${area.slug}`} />
      </section>

      {/* OTHER SERVICES + NEARBY */}
      <section className="border-t border-[#27272A] bg-[#0E0E10]" data-testid="subarea-explore-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 space-y-12">
          <FadeUp>
            <p className="font-telemetry text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
              Other services in {d}
            </p>
            <div className="flex flex-wrap gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/services/${o.slug}/in/${area.slug}`}
                  data-testid={`subarea-other-service-${o.slug}`}
                  className="group flex items-center gap-2 px-5 py-3 rounded-full border border-[#27272A] hover:border-[#FF5500]/60 transition-colors text-sm text-zinc-300 hover:text-white"
                >
                  {o.shortName} in {d}
                  <ArrowRight className="w-3.5 h-3.5 text-[#FF5500] transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </FadeUp>
          <FadeUp>
            <p className="font-telemetry text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
              Nearby areas in {town.name}
            </p>
            <div className="flex flex-wrap gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/in/${n.slug}`}
                  data-testid={`subarea-nearby-${n.slug}`}
                  className="px-5 py-3 rounded-full border border-[#27272A] hover:border-[#FF5500]/60 transition-colors text-sm text-zinc-300 hover:text-white"
                >
                  {n.district}
                </Link>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBanner
        title={`NEED HELP IN ${d.toUpperCase()}?`}
        sub={`${service.shortName} across ${area.district} and ${town.name} — typical response ${town.eta}, 24/7.`}
      />

      {/* BOOKING */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="subarea-booking-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeUp>
            <Eyebrow>Book in {d}</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl mt-3 leading-[0.95]">
              Get {service.shortName.toLowerCase()}
              <br />
              <span className="text-[#FF5500]">in {d} today.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              The form sends your enquiry — with your live location if you allow it — straight to our
              dispatch WhatsApp. A human replies, usually within minutes.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ContactForm defaultService={service.shortName} />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
